import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authApi, formatApiError } from "../../lib/authApi";
import { setMemberSession } from "../../hooks/useMemberAuth";
import { AuthShell, Field, PrimaryBtn, SmallLink, Notice } from "../../components/auth/AuthShell";

const INVALID_LEADS = {
  used: "This reset link has already been used. For security, each link works exactly once.",
  expired: "This reset link has expired.",
  invalid: "This reset link is invalid — it may have been superseded by a newer request.",
};

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // checking | valid | invalid — resolved by a READ-ONLY validate call.
  const [linkState, setLinkState] = useState("checking");
  const [reason, setReason] = useState("invalid");
  const [knownEmail, setKnownEmail] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await authApi.validateResetToken(token);
        if (!alive) return;
        if (data.valid) {
          setLinkState("valid");
        } else {
          setReason(data.reason || "invalid");
          setKnownEmail(data.email || null);
          setLinkState("invalid");
        }
      } catch {
        // Network hiccup — show the form; the POST is authoritative.
        if (alive) setLinkState("valid");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    if (pw !== pw2) { setErr("Passwords do not match."); setBusy(false); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); setBusy(false); return; }
    try {
      const { data } = await authApi.resetPassword({ token, password: pw, password_confirm: pw2 });
      if (data.session_token) await setMemberSession(data.session_token);
      nav("/members");
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail) || "Reset failed.";
      // Token-state failures switch to the locked, non-editable view.
      if (/invalid or already used/i.test(detail)) {
        setReason("used"); setLinkState("invalid");
      } else if (/expired/i.test(detail)) {
        setReason("expired"); setLinkState("invalid");
      } else {
        setErr(detail);
      }
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" lead="This link is missing its token. Request a new reset email.">
        <PrimaryBtn type="button" data-testid="reset-request-new"
                    onClick={() => nav("/auth/forgot-password")}>
          REQUEST A NEW RESET LINK
        </PrimaryBtn>
        <div className="mt-4">
          <SmallLink to="/auth/forgot-password" data-testid="reset-go-forgot">START OVER</SmallLink>
        </div>
      </AuthShell>
    );
  }

  if (linkState === "checking") {
    return (
      <AuthShell title="Set a new password" lead="Verifying your reset link…">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39E93]"
             data-testid="reset-checking">
          // CHECKING LINK…
        </div>
      </AuthShell>
    );
  }

  if (linkState === "invalid") {
    return (
      <AuthShell
        title="Reset link unavailable"
        lead={INVALID_LEADS[reason] || INVALID_LEADS.invalid}
        footer={<SmallLink to="/auth/login" data-testid="reset-back-login">BACK TO SIGN IN</SmallLink>}
      >
        <Notice kind="error" data-testid="reset-invalid-notice">
          {reason === "expired"
            ? "Reset links expire for your protection. Request a fresh one below — it takes seconds."
            : "For your security this link can no longer be used. Request a fresh one below — it takes seconds."}
        </Notice>
        <PrimaryBtn
          type="button"
          data-testid="reset-request-new"
          onClick={() => nav("/auth/forgot-password", { state: { email: knownEmail } })}
        >
          REQUEST A NEW RESET LINK
        </PrimaryBtn>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      lead="Choose a new password. All existing sessions on this account will be signed out."
      footer={<SmallLink to="/auth/login" data-testid="reset-back-login">BACK TO SIGN IN</SmallLink>}
    >
      {err && <Notice kind="error" data-testid="reset-error">{err}</Notice>}
      <form onSubmit={onSubmit} data-testid="reset-form">
        <Field label="NEW PASSWORD" type="password" value={pw} onChange={setPw}
               required autoFocus autoComplete="new-password"
               data-testid="reset-password" name="password" />
        <Field label="CONFIRM" type="password" value={pw2} onChange={setPw2}
               required autoComplete="new-password"
               data-testid="reset-password-confirm" name="password_confirm" />
        <PrimaryBtn disabled={busy} data-testid="reset-submit">
          {busy ? "UPDATING…" : "SET NEW PASSWORD"}
        </PrimaryBtn>
      </form>
    </AuthShell>
  );
};

export default ResetPasswordPage;
